import * as moment from 'moment';
import * as _ from 'lodash';
import { useEffect, useState, ChangeEvent } from 'react';
import { IListItem, IDonorWithRemoteRecipient, IDonorWithMSRecipientUpdated } from '@nkr/models/index';
import { Alert, Button, FormControl, FormGroup, Modal, OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import { ICoordinatorListByCenter } from '@nkr/models/coordinator-list-by-center';
import MaskedInput from 'react-maskedinput';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const TableWrapper = styled(Table)`
    margin-bottom: 0px;

    & > thead > tr > th {
        background-color: #ddd;
    }
    & > thead > tr > th,
    & > tbody > tr > td {
        text-align: center;
        vertical-align: middle;
        font-size: 12px;
    }
`;

interface IProps {
    show: boolean,
    list: IDonorWithRemoteRecipient[],
    isAdmin: boolean,
    centerId: number,
    statusList: IListItem[],
    coordinatorList: ICoordinatorListByCenter[],
    onSave: (updatedList: IDonorWithMSRecipientUpdated[]) => void
    onClose: () => void,
}

interface IDonorWithRemoteRecipientExtended extends IDonorWithRemoteRecipient {
    originalCoordinatorId: number,
    originalRemoteStatus: number,
    originalComments: string,
    originalRemoteStatusDate: string,
    isStatusChanged: boolean,
    isCoordinatorChanged: boolean,
    isCommentChanged: boolean,
    isStatusDateChanged: boolean,
    editComments: boolean
}

const DonorWithRemotePatients = (props: IProps) => {
    const [list, setList] = useState<IDonorWithRemoteRecipientExtended[]>([]);

    useEffect(() => {
        let newList: IDonorWithRemoteRecipientExtended[] = props.list.map(x => {
            let oneMonthAgo = moment().subtract(1, 'months');

            return {
                ...x,
                isStatusChanged: false,
                isCoordinatorChanged: false,
                isCommentChanged: false,
                isStatusDateChanged: false,
                remoteStatus: x.remoteStatus,
                originalCoordinatorId: x.coordinatorId,
                originalRemoteStatus: x.remoteStatus,
                originalComments: x.comments,
                originalRemoteStatusDate: x.remoteStatusDate,
                editComments: false
            }
        });

        setList(newList);
    }, [props.list]);

    const handleStatusChange = (id: number, status: number) => {
        let newList = _.cloneDeep(list).map(d => {
            if (d.id == id) {
                let changed = d.originalRemoteStatus != status;
                let isDisabled = (status == -1 || (status != 10 && status != 25 && status != 100 && status != -1));

                return {
                    ...d,
                    remoteStatus: status,
                    isStatusChanged: changed,
                    remoteStatusDate: isDisabled ? null : d.remoteStatusDate
                };
            }

            return d;
        });

        setList(newList);
    };

    const handleCoordinatorChange = (id: number, coordinatorId: number) => {
        let newList = _.cloneDeep(list).map(d => {
            if (d.id == id) {
                let changed = d.originalCoordinatorId != coordinatorId;

                return {
                    ...d,
                    coordinatorId,
                    isCoordinatorChanged: changed,
                    remoteStatus: coordinatorId == -1 ? -1 : d.remoteStatus,
                    isStatusUpdated: coordinatorId == -1 ? false : null
                };
            }

            return d;
        });

        setList(newList);
    };

    const handleStatusDateChange = (id: number, statusDate: string) => {
        let newList = _.cloneDeep(list).map(d => {
            if (d.id == id) {
                let changed = d.originalRemoteStatusDate != statusDate;

                return {
                    ...d,
                    remoteStatusDate: statusDate,
                    isStatusDateChanged: changed,
                };
            }

            return d;
        });

        setList(newList);
    };

    const handleCommentsChange = (id: number, comments: string) => {
        let newList = _.cloneDeep(list).map(d => {
            if (d.id == id) {
                let changed = d.originalComments != comments;

                return {
                    ...d,
                    comments,
                    isCommentChanged: changed,
                };
            }
            return d;
        });

        setList(newList);
    };

    const canUpdateRow = (donor: IDonorWithRemoteRecipientExtended): boolean => {
        const requiresDate       = [10,25,100].includes(donor.remoteStatus);
        return donor.remoteStatus >= 0 && (!requiresDate || isValidDate(donor.remoteStatusDate));
    }

    const isValidDate = (date) => {
        let parsedDate = new Date(date);

        if (isNaN(parsedDate.getTime())) {
            return false; // Invalid date
        }

        let year = parsedDate.getFullYear();

        return year >= 1900 && year <= 2100; // Check if the year is within a realistic range
    }

    const handleInvalidDates = (donorId?: number) => {
        return list.filter(donor => {
            let isDisabled = donor.remoteStatus == -1 || ![10, 25, 100, -1].includes(donor.remoteStatus);
            let isDateInvalid = !donor.remoteStatusDate || donor.remoteStatusDate == '' || !isValidDate(donor.remoteStatusDate);
            let isMatchingId = donorId == undefined || donor.id == donorId;

            return isDateInvalid && donor.isStatusChanged && !isDisabled && isMatchingId;
        });
    };

    const markInvalidDonors = (donorId?: number) => {
        let newList = list.map(donor => ({
            ...donor,
            isStatusUpdated: (!donor.remoteStatusDate || donor.remoteStatusDate == '' || !isValidDate(donor.remoteStatusDate)) && donor.isStatusChanged
        }));

        setList(newList);
    };

    const getUpdatedList = (donorId?: number): IDonorWithMSRecipientUpdated[] => {
        return _.cloneDeep(list)
            .filter(donor => {
                let hasUpdates =
                    donor.isStatusChanged ||
                    donor.isCoordinatorChanged ||
                    donor.isCommentChanged ||
                    donor.isStatusDateChanged;

                let isMatchingId = donorId == undefined || donor.id == donorId;

                return hasUpdates && isMatchingId;
            })
            .map(donor => ({
                id: donor.id,
                coordinatorId: donor.coordinatorId,
                remoteStatus: donor.remoteStatus,
                comments: donor.comments,
                remoteStatusDate: donor.remoteStatusDate,
            }));
    };

    const handleBulkSave = () => {
        let invalidDates = handleInvalidDates();

        if (invalidDates.length > 0) {
            markInvalidDonors();
            return; // Prevent saving if there are invalid fields
        }

        let updatedList = getUpdatedList();

        props.onSave(updatedList);
    };

    const handleSave = (donorId: number) => {
        const donor = list.find(d => d.id == donorId);

        if( canUpdateRow(donor) ) {
            props.onSave( [donor] );
        }
        else {
            toast.error('Cannot save record. Status or required date missing.');
        }
    };

    const allowBulkUpdate = (): boolean => {
        let allowUpdate = list.some(donor =>
            donor.isCoordinatorChanged ||
            donor.isStatusChanged ||
            donor.isCommentChanged ||
            donor.isStatusDateChanged
        );

        return allowUpdate;
    };

    const isChanged = allowBulkUpdate();

    return (
        <Modal backdrop='static' className='modal-xxxl' bsSize='lg' show={props.show} onHide={props.onClose}>
            <Modal.Header closeButton>
                <Modal.Title>Donors Registered With Intended Recipients at Other Centers</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert bsStyle='info' bsSize='sm'>
                    Please keep the remote intended recipient center informed of the current status of the donor candidates so that they can keep the patient
                    properly informed. While in progress, just click "Update" to mute the alert for two weeks. Once the candidate has donated or is
                    Ruled Out, that status will end the request for updates.
                </Alert>

                <div style={{ maxHeight: 500, overflowY: 'scroll' }}>
                    <TableWrapper condensed bordered>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 999999 }}>
                            <tr>
                                <th>R#</th>
                                <th>Donor Registration</th>
                                <th>Intended Recipient Center</th>
                                <th>Intended Recipient</th>
                                <th>Last Updated</th>
                                <th>Donor Coordinator</th>
                                <th>Network<br />Status</th>
                                <th>Status Date</th>
                                <th>Status Comments</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((item, index) => {
                                let isCoordinatorDisabled = item.coordinatorId == -1;
                                let hasStatusDate = [10, 25, 100];

                                let isStatusDateDisabled = (item.remoteStatus == -1 || (item.remoteStatus != 10 && item.remoteStatus != 25 && item.remoteStatus != 100 && item.remoteStatus != -1));
                                let canSave              = canUpdateRow(item);

                                let tooltip = (
                                    <Tooltip id={`tooltip-${item.id}`} className='left-aligned-tooltip'>
                                        {item.comments || 'No comment'}
                                    </Tooltip>
                                );

                                return (
                                    <tr key={'do-' + item.id}>
                                        <td>{index + 1}</td>
                                        <td><a href={`center/${props.centerId}/donors/donor-intake/details/${item.donorId}`} target='_blank'>{item.fullName}</a></td>
                                        <td>{item.patientCenter}</td>
                                        <td>{item.patientName}</td>
                                        <td>{item.remoteStatusLastUpdatedDate}</td>
                                        <td>
                                            <FormControl
                                                componentClass='select'
                                                bsSize='xs'
                                                value={item.coordinatorId}
                                                onChange={(e: ChangeEvent<FormControl & HTMLSelectElement>) => handleCoordinatorChange(item.id, parseInt(e.currentTarget.value))}
                                            >
                                                <option value={-1}>- Not Assigned -</option>
                                                {props.coordinatorList != null && (
                                                    props.coordinatorList[0].list.map((item) => {
                                                        return <option key={_.uniqueId("co")} value={item.contactId}>{item.fullName}</option>
                                                    })
                                                )}
                                            </FormControl>
                                        </td>
                                        <td>
                                            <FormControl
                                                componentClass="select"
                                                bsSize="xs"
                                                value={item.remoteStatus}
                                                onChange={(e: ChangeEvent<FormControl & HTMLSelectElement>) => handleStatusChange(item.id, parseInt(e.currentTarget.value))}
                                                disabled={isCoordinatorDisabled}
                                            >
                                                <option value={-1}>- Please Update -</option>
                                                {props.statusList.map(status => (
                                                    <option
                                                        key={'st-' + status.id}
                                                        value={status.id}
                                                        style={{ color: hasStatusDate.includes(status.id) ? '#7aa7cf' : '' }}
                                                    >
                                                        {status.description}
                                                    </option>
                                                ))}
                                            </FormControl>
                                        </td>
                                        <td>
                                            {isStatusDateDisabled && item.remoteStatus != -1 ?
                                                <><span style={{ fontWeight: 'bold' }}>Not Required</span></>
                                                :

                                                <FormGroup
                                                    style={{ marginBottom: 0 }}
                                                    validationState={item.isStatusChanged && (!item.remoteStatusDate || !isValidDate(item.remoteStatusDate)) ? 'error' : null}
                                                >
                                                    <MaskedInput
                                                        id='remoteStatusDate'
                                                        name='remoteStatusDate'
                                                        className='form-control'

                                                        style={{
                                                            width: '100%',
                                                            height: '20px',
                                                            fontSize: '12px'
                                                        }}
                                                        disabled={isStatusDateDisabled}
                                                        mask={'11/11/1111'}
                                                        value={item.remoteStatusDate}
                                                        onChange={(e: ChangeEvent<FormControl & HTMLInputElement>) => handleStatusDateChange(item.id, e.currentTarget.value)}
                                                    />
                                                </FormGroup>
                                            }
                                        </td>
                                        <td>
                                            <OverlayTrigger
                                                placement="top"
                                                trigger={['hover', 'focus']}
                                                overlay={tooltip}
                                            >
                                                <FormControl
                                                    type='text'
                                                    placeholder='intended recipient center visibility'
                                                    value={item.comments}
                                                    onChange={(e: React.ChangeEvent<FormControl & HTMLInputElement>) => handleCommentsChange(item.id, e.currentTarget.value)}
                                                    onBlur={() => null}
                                                    bsSize='xs'
                                                    maxLength={250}
                                                />
                                            </OverlayTrigger>
                                        </td>
                                        <td>
                                            <Button bsStyle='primary' bsSize='xs' disabled={!canSave} onClick={() => handleSave(item.id)}>Update</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </TableWrapper>
                </div>
                <div>{list.length} registrations...</div>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='success' disabled={!isChanged} onClick={handleBulkSave}>Bulk Update Record(s)</Button>
                <Button onClick={props.onClose}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default DonorWithRemotePatients;